<template>
  <div
    class="flex items-center w-full min-h-[40px] h-full relative group/option"
    :class="{ 'cursor-pointer': readonly }"
    @mouseenter="!disabled && (isHovered = true)"
    @mouseleave="isHovered = false"
    @click="readonly && !disabled && emit('select-item', item.id)">
    <div
      class="absolute w-full h-full transition-all duration-300 border border-secondary-500 rounded-md"
      :class="[
        {
          'bg-primary-200 !border-primary-600 !border-1': selected,
          '!border-primary-600':
            (isHeadLineFocused && !selected) || (isHovered && !selected),
          '!w-[calc(100%-1.8rem)]':
            !disabled &&
            !readonly &&
            allowDelete &&
            (isHovered || isHeadLineFocused),
        },
      ]"></div>
    <div
      v-if="!disabled && !readonly && allowDelete"
      class="overflow-hidden flex-shrink-0 z-10"
      :class="['order-2']">
      <button
        class="transition-all duration-500 flex items-center justify-center bg-secondary-200 rounded-full cursor-pointer w-5 h-5 hover:bg-secondary-400"
        :class="[isHovered || isHeadLineFocused ? 'opacity-100' : 'opacity-0']"
        @click.stop="$emit('remove-option')">
        <X class="text-secondary-900" :size="12" />
      </button>
    </div>
    <div
      class="flex items-center py-0 px-3 relative min-h-[40px] h-full flex-grow overflow-hidden order-1 z-10"
      :class="[
        {
          '!pl-6': rtl,
          '!pr-6': !rtl,
        },
        {
          '!h-[calc(100%-2px)] !min-h-[38px]': format === 'letter',
          '!pr-0 pl-3 rounded-r-md mr-[1px]': format === 'letter' && rtl,
          '!pl-0 pr-3 rounded-l-md ml-[1px]': format === 'letter' && !rtl,
        },
      ]">
      <div class="flex flex-1 items-center gap-2.5 h-full overflow-hidden">
        <div
          class="flex items-center justify-center h-full flex-shrink-0"
          :class="{ 'items-stretch': format === 'letter' }"
          @click="!readonly && emit('select-item', item.id)">
          <template v-if="format === 'button'">
            <Checkbox
              v-if="multiSelect"
              v-model="checkboxValue"
              binary
              :disabled="isCheckboxDisabled"
              :pt="{
                box: ({ props, context }) => ({
                  class: [
                    // Colors
                    {
                      '!bg-primary': context.checked,
                      '!border-primary-600': !context.checked && isHovered,
                    },
                    {
                      '[&>svg]:text-white [&>svg]:w-[0.65rem] [&>svg]:h-[0.65rem]':
                        context.checked,
                    },
                  ],
                }),
              }"
              :pt-options="{ mergeSections: false, mergeProps: true }"
              @click.stop>
              <template #icon="{ checked }">
                <Check v-if="checked" :stroke-width="4" :size="16" />
              </template>
            </Checkbox>
            <RadioIcon
              v-else
              v-model="radioValue"
              :class="{ '!border-primary-600': isHovered }"
              :model-name="'selected'"
              :is-disabled="disabled || (exceededMaxSelections && !selected)"/>
          </template>
          <template v-if="format === 'letter'">
            <div
              class="bg-secondary-100 text-secondary-900 w-[28px] min-h-[33px] h-full flex items-center justify-center border-secondary-400 cursor-pointer transition-all duration-200 ease-in-out"
              :class="{
                '!bg-primary !text-white !border-primary hover:!bg-primary-700':
                  selected,
                '!bg-secondary-100 !text-secondary-900 !border-secondary-400':
                  !selected && !isHovered,
                '!bg-white !border-primary-600': !selected && isHovered,
                'border-r': !rtl,
                'border-l': rtl,
              }">
              <span
                class="inline-block align-middle leading-none font-bold text-sm text-center"
                :class="{ 'mt-0.5': !rtl }">
                {{ getLetter(index) }}
              </span>
            </div>
          </template>
        </div>
        <div class="flex-1 min-w-0 overflow-hidden">
          <p
            v-if="readonly && !item.title && !otherTitle"
            class="text-red-500 text-sm py-2">
            {{ 'חסר טקסט' }}
          </p>
          <HeadLine
            v-else
            :id="`${item.id}-${readonly}`"
            v-model="titleEdited"
            :closable="false"
            :editable="!readonly || item.editable || false"
            visual-boundary="none"
            :placeholder="
              item.editable && readonly ? item.title : textPlaceholderLabel
            "
            class="h-full my-1.5 w-full"
            :options="headlineOptions"
            @update="handleTitleChange"
            @focus="isHeadLineFocused = true"
            @blur="isHeadLineFocused = false"
            @click="otherTitle && $event.stopPropagation()"/>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { type PropType, ref, computed, inject, watch } from 'vue';
import Checkbox from 'primevue/checkbox';
import RadioIcon from '@/components/Elements/RadioIcon.vue';
import { Check, X } from 'lucide-vue-next';
import { HeadLine } from '@amit/components';
import { ChoiceOptionType } from '@amit/types/src/blocks/choice';
import { isTextEmpty } from '@amit/tiptap';
import { KeyboardSettings } from '@amit/types';

const isHovered = ref(false);
const isHeadLineFocused = ref(false);

const props = defineProps({
  item: {
    type: Object as PropType<ChoiceOptionType>,
    required: true,
  },
  selected: {
    type: Boolean,
    required: false,
  },
  disabled: {
    type: Boolean,
    required: false,
  },
  format: {
    type: String as PropType<
      'text' | 'image' | 'text-image' | 'image-text' | 'letter' | 'button'
    >,
    default: 'button',
  },
  rtl: {
    type: Boolean,
    default: true,
  },
  multiSelect: {
    type: Boolean,
    default: false,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  index: {
    type: Number,
    default: 0,
  },
  textPlaceholderLabel: {
    type: String,
    default: 'פה כדאי להכניס טקסט',
  },
  allowDelete: {
    type: Boolean,
    default: true,
  },
  exceededMaxSelections: {
    type: Boolean,
    default: false,
  },
  otherTitle: {
    type: String,
    default: '',
  },
});

const keyboardSettings = inject(
  'keyboardSettings',
  computed(() => ({
    showMathmaticalKeyboard: false,
    keyboardList: [],
  })),
);
const emit = defineEmits(['remove-option', 'edit-item-text', 'select-item']);

// Handle selection state changes
const handleSelection = () => {
  // Don't allow selection if max selections are exceeded and this item isn't already selected
  if (props.exceededMaxSelections && !props.selected) return;
  emit('select-item', props.item.id);
};

const checkboxValue = computed({
  get: () => props.selected,
  set: value => {
    // Only allow setting to true if we're not at max selections or if this item is already selected
    if (value && props.exceededMaxSelections && !props.selected) {
      return; // Prevent the change
    }
    handleSelection();
  },
});

const radioValue = computed({
  get: () => props.selected,
  set: () => handleSelection(),
});

const titleEdited = ref(
  props.item.editable && props.readonly ? props.otherTitle : props.item.title,
);

watch(
  () => props.otherTitle,
  () => {
    titleEdited.value =
      props.item.editable && props.readonly
        ? props.otherTitle
        : props.item.title;
  },
);

const handleTitleChange = () => {
  if (isTextEmpty(titleEdited.value)) {
    emit('edit-item-text', '<p></p>');
  } else if (titleEdited.value !== props.item.title) {
    emit('edit-item-text', titleEdited.value);
  }
};

const getLetter = (index: number) => {
  if (props.rtl) {
    // Hebrew letters (alef, bet, gimel...)
    const hebrewLetters = 'אבגדהוזחטיכסעפצקרשתףץךםן';
    return hebrewLetters[index % hebrewLetters.length];
  } else {
    // English letters (A, B, C...)
    return String.fromCharCode(65 + (index % 26));
  }
};

// Add a computed property to determine if checkbox should be disabled
const isCheckboxDisabled = computed(() => {
  return props.disabled || (props.exceededMaxSelections && !props.selected);
});
const headlineOptions = computed(() => {
  const baseOptions = {
    defaultNode: 'headline' as 'headline',
    sizeBreakpoint: 0,
    fontSize: '14px',
    lineHeight: 1.2,
    textAlign: 'inherit',
  };

  const finalKeyboardSettings: KeyboardSettings = { ...keyboardSettings.value };

  if (props.item.editable) {
    baseOptions['keyboardSettings'] = finalKeyboardSettings;
  }

  return {
    ...baseOptions,
  };
});
</script>
